import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import MonthsDropDown from "../Shared/MonthsDropDown";
import YearsDropDown from "../Shared/YearsDropDown";
import FeeSystemMenu from "./FeeSystemMenu";
import config from "../../Config";

const computeReceiveable = (feePosted, concession, arrears) => {
  const fee = parseFloat(feePosted) || 0;
  const conc = parseFloat(concession) || 0;
  const arr = parseFloat(arrears) || 0;
  let base = fee - conc;
  if (base < 0) base = 0;
  return base + arr;
};

const pickLineValue = (line, ...keys) => {
  if (!line) return undefined;
  for (const key of keys) {
    const value = line[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

const normalizePreviewLine = (line) => {
  const feeCode = String(pickLineValue(line, "Fee_Code", "fee_Code", "FeeCode") || "").trim();
  const feeType = pickLineValue(line, "Fee_Type_ID", "fee_Type_ID", "FeeType_ID");
  return {
    Line_Id:
      pickLineValue(line, "Line_Id", "line_Id") ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    Fee_Type_ID: parseInt(feeType, 10) || 0,
    Fee_Code: feeCode,
    Description: pickLineValue(line, "Description", "description") || "",
    Fee_Posted: parseFloat(pickLineValue(line, "Fee_Posted", "fee_Posted")) || 0,
    Concession_Amount:
      parseFloat(pickLineValue(line, "Concession_Amount", "concession_Amount")) || 0,
    Concession_Code: pickLineValue(line, "Concession_Code", "concession_Code") || "001",
    Arrears: parseFloat(pickLineValue(line, "Arrears", "arrears")) || 0,
    Receiveable_Fee:
      parseFloat(pickLineValue(line, "Receiveable_Fee", "receiveable_Fee")) || 0,
    Bus_Route_Code: pickLineValue(line, "Bus_Route_Code", "bus_Route_Code") || "",
    Bus_Monthly_Days:
      parseInt(pickLineValue(line, "Bus_Monthly_Days", "bus_Monthly_Days"), 10) || 0,
    Bus_Avail_Days:
      parseInt(pickLineValue(line, "Bus_Avail_Days", "bus_Avail_Days"), 10) || 0,
    Is_Manual: !!pickLineValue(line, "Is_Manual", "is_Manual"),
  };
};

const formatApiError = (responseData, fallback) => {
  const message =
    responseData?.error_message ||
    responseData?.status_message ||
    fallback;
  return message;
};

const newPreviewLine = () => ({
  Line_Id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  Fee_Type_ID: 0,
  Fee_Code: "",
  Description: "",
  Fee_Posted: 0,
  Concession_Amount: 0,
  Concession_Code: "001",
  Arrears: 0,
  Receiveable_Fee: 0,
  Bus_Route_Code: "",
  Bus_Monthly_Days: 0,
  Bus_Avail_Days: 0,
  Is_Manual: true,
});

const GenerateChallan = () => {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [UserName] = useState(localStorage.getItem("UserName") || "");

  const [RegistrationNo, setRegistrationNo] = useState("");
  const [StudentId, setStudentId] = useState("");
  const [studentInfo, setStudentInfo] = useState(null);
  const [studentConcessions, setStudentConcessions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [SelectedMonth, setSelectedMonth] = useState("");
  const [FYear, setFYear] = useState("");
  const [ChallanDueDate, setChallanDueDate] = useState("");
  const [AddTransportFee, setAddTransportFee] = useState(false);
  const [AdjustAdvanceOnChallan, setAdjustAdvanceOnChallan] = useState(false);
  const [RemainingAdvance, setRemainingAdvance] = useState(0);
  const [lastAdvanceAdjusted, setLastAdvanceAdjusted] = useState(0);
  const [lastChallanBalance, setLastChallanBalance] = useState(0);
  const [BusTotalDays, setBusTotalDays] = useState(0);
  const [BusAvailDays, setBusAvailDays] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [previewLines, setPreviewLines] = useState([]);
  const [availableFeeTypes, setAvailableFeeTypes] = useState([]);
  const [generatedChallan, setGeneratedChallan] = useState("");

  const location = useLocation();
  const hasAutoSearched = useRef(false);

  const clearPreview = () => {
    setPreviewReady(false);
    setPreviewLines([]);
    setAvailableFeeTypes([]);
    setGeneratedChallan("");
    setLastAdvanceAdjusted(0);
    setLastChallanBalance(0);
  };

  const loadStudentAdvanceBalance = async (studentIdValue) => {
    if (!studentIdValue) {
      setRemainingAdvance(0);
      return;
    }
    try {
      const resp = await axios({
        method: "post",
        url: config.base_url + "ADLedger/GetStudentAdvanceBalance",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey,
          UserID,
          MemberType,
          Student_ID: parseInt(studentIdValue, 10),
        },
      });
      if (resp.data.status_code === 1) {
        setRemainingAdvance(resp.data.RemainingAdvance || 0);
      } else {
        setRemainingAdvance(0);
      }
    } catch {
      setRemainingAdvance(0);
    }
  };

  const applyStudentDetails = async (student, concessions) => {
    clearPreview();
    setStudentInfo(student);
    setStudentConcessions(concessions || []);
    setAdjustAdvanceOnChallan(false);
    if (student?.Student_ID) {
      await loadStudentAdvanceBalance(student.Student_ID);
    } else {
      setRemainingAdvance(0);
    }
  };

  const buildChallanRequest = (includeLines = false) => {
    const payload = {
      AccessKey,
      UserID,
      MemberType,
      UserName,
      Registration_No: studentInfo?.Registration_No || "",
      Student_ID: studentInfo?.Student_ID,
      SelectedMonth: parseInt(SelectedMonth, 10),
      FYear: parseInt(FYear, 10),
      Challan_DueDate: ChallanDueDate ? `${ChallanDueDate}T00:00:00` : null,
      AddTransportFee,
      BusTotalDays: parseInt(BusTotalDays, 10) || 0,
      BusAvailDays: parseInt(BusAvailDays, 10) || 0,
      AdjustAdvanceOnChallan,
    };

    if (includeLines) {
      payload.ChallanLines = previewLines
        .map((line) => normalizePreviewLine(line))
        .filter((line) => line.Fee_Code)
        .map((line) => ({
          Line_Id: line.Line_Id,
          Fee_Type_ID: line.Fee_Type_ID || 0,
          Fee_Code: line.Fee_Code,
          Description: line.Description || "",
          Fee_Posted: line.Fee_Posted,
          Concession_Amount: line.Concession_Amount,
          Concession_Code: line.Concession_Code || "001",
          Arrears: line.Arrears,
          Receiveable_Fee:
            line.Receiveable_Fee > 0
              ? line.Receiveable_Fee
              : computeReceiveable(
                  line.Fee_Posted,
                  line.Concession_Amount,
                  line.Arrears
                ),
          Bus_Route_Code: line.Bus_Route_Code || "",
          Bus_Monthly_Days: line.Bus_Monthly_Days,
          Bus_Avail_Days: line.Bus_Avail_Days,
          Is_Manual: !!line.Is_Manual,
        }));
    }

    return payload;
  };

  const previewTotal = previewLines.reduce((sum, line) => {
    const normalized = normalizePreviewLine(line);
    const amount =
      normalized.Receiveable_Fee > 0
        ? normalized.Receiveable_Fee
        : computeReceiveable(
            normalized.Fee_Posted,
            normalized.Concession_Amount,
            normalized.Arrears
          );
    return sum + amount;
  }, 0);

  const validateFilters = () => {
    if (!studentInfo || !SelectedMonth || !FYear) {
      toast.error("Please select student, month and year.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    return true;
  };

  const updatePreviewLine = (index, field, value) => {
    setPreviewLines((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const updated = { ...row, [field]: value };
        if (
          field === "Fee_Posted" ||
          field === "Concession_Amount" ||
          field === "Arrears"
        ) {
          updated.Receiveable_Fee = computeReceiveable(
            updated.Fee_Posted,
            updated.Concession_Amount,
            updated.Arrears
          );
        }
        return updated;
      })
    );
  };

  const handleFeeTypeChange = (index, feeCode) => {
    const fee = availableFeeTypes.find(
      (ft) => String(ft.Fee_Type_Code) === String(feeCode)
    );
    setPreviewLines((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const updated = {
          ...row,
          Fee_Code: feeCode,
          Fee_Type_ID: fee ? fee.Fee_Type_ID : 0,
          Description: fee ? fee.Fee_Type_Name : row.Description,
        };
        updated.Receiveable_Fee = computeReceiveable(
          updated.Fee_Posted,
          updated.Concession_Amount,
          updated.Arrears
        );
        return updated;
      })
    );
  };

  const handleAddPreviewRow = () => {
    setPreviewLines((prev) => [...prev, newPreviewLine()]);
    setPreviewReady(true);
  };

  const handleRemovePreviewRow = (index) => {
    setPreviewLines((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    document.title = "Generate Challan";

    const params = new URLSearchParams(location.search);
    const urlStudentId = params.get("Student_ID");
    const urlMonth = params.get("Month");
    const urlYear = params.get("Year");

    const now = new Date();
    setSelectedMonth(urlMonth || (now.getMonth() + 1).toString());
    setFYear(urlYear || now.getFullYear().toString());

    if (urlStudentId && !hasAutoSearched.current) {
      hasAutoSearched.current = true;
      setStudentId(urlStudentId);
      performAutoSearch(urlStudentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (previewReady) {
      clearPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    SelectedMonth,
    FYear,
    ChallanDueDate,
    AddTransportFee,
    BusTotalDays,
    BusAvailDays,
  ]);

  const performAutoSearch = async (studentIdValue) => {
    setSearchLoading(true);
    try {
      const detailResp = await axios({
        method: "post",
        url: config.base_url + "Student/GetStudentByID",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey,
          UserID,
          MemberType,
          ID: studentIdValue,
        },
      });

      if (detailResp.data.status_code === 1 && detailResp.data.Student) {
        await applyStudentDetails(
          detailResp.data.Student,
          detailResp.data.ListOfMultiConcessions
        );
        toast.success("Student loaded successfully!", {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(detailResp.data.status_message || "Student not found.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch {
      toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setStudentInfo(null);
    setStudentConcessions([]);
    clearPreview();

    if (!RegistrationNo && !StudentId) {
      toast.error("Please enter registration number or student ID.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setSearchLoading(true);
    try {
      let studentIdValue = StudentId;
      if (!studentIdValue && RegistrationNo) {
        const regResp = await axios({
          method: "post",
          url: config.base_url + "Student/GetStudentByRegistrationNo",
          headers: { "Content-Type": "application/json" },
          data: {
            AccessKey,
            UserID,
            MemberType,
            Registration_No: RegistrationNo,
          },
        });
        if (regResp.data.status_code !== 1 || !regResp.data.Student) {
          toast.error(regResp.data.status_message || "Student not found.", {
            position: toast.POSITION.TOP_RIGHT,
          });
          setSearchLoading(false);
          return;
        }
        studentIdValue = regResp.data.Student.Student_ID;
      }

      const detailResp = await axios({
        method: "post",
        url: config.base_url + "Student/GetStudentByID",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey,
          UserID,
          MemberType,
          ID: studentIdValue,
        },
      });

      if (detailResp.data.status_code === 1 && detailResp.data.Student) {
        await applyStudentDetails(
          detailResp.data.Student,
          detailResp.data.ListOfMultiConcessions
        );
      } else {
        toast.error(detailResp.data.status_message || "Student not found.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch {
      toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePreview = async (e) => {
    e.preventDefault();
    if (!validateFilters()) return;

    setPreviewLoading(true);
    clearPreview();
    try {
      const response = await axios({
        method: "post",
        url: config.base_url + "ADLedger/PreviewChallanForStudent",
        headers: { "Content-Type": "application/json" },
        data: buildChallanRequest(false),
      });

      if (response.data.status_code === 1 && response.data.Preview) {
        const preview = response.data.Preview;
        const normalizedLines = (preview.Lines || []).map((line) => {
          const normalized = normalizePreviewLine(line);
          if (normalized.Receiveable_Fee <= 0) {
            normalized.Receiveable_Fee = computeReceiveable(
              normalized.Fee_Posted,
              normalized.Concession_Amount,
              normalized.Arrears
            );
          }
          return normalized;
        });
        setPreviewLines(normalizedLines);
        setAvailableFeeTypes(preview.Available_Fee_Types || []);
        setPreviewReady(true);
        toast.success(response.data.status_message || "Preview ready.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(response.data.status_message || "Unable to load preview.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch {
      toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!validateFilters()) return;

    const validLines = previewLines
      .map((line) => normalizePreviewLine(line))
      .filter((line) => line.Fee_Code);
    if (validLines.length === 0) {
      toast.error("Add at least one fee head in the preview before generating.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setLoading(true);
    setGeneratedChallan("");
    try {
      const response = await axios({
        method: "post",
        url: config.base_url + "ADLedger/GenerateChallanForStudent",
        headers: { "Content-Type": "application/json" },
        data: buildChallanRequest(true),
      });

      if (response.data.status_code === 1) {
        setGeneratedChallan(response.data.Challan_No || "");
        setLastAdvanceAdjusted(response.data.Advance_Adjusted || 0);
        setLastChallanBalance(
          response.data.Challan_Balance != null ? response.data.Challan_Balance : 0
        );
        if (studentInfo?.Student_ID) {
          loadStudentAdvanceBalance(studentInfo.Student_ID);
        }
        toast.success(response.data.status_message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else {
        toast.error(formatApiError(response.data, "Unable to generate challan."), {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      const apiMessage = error?.response?.data
        ? formatApiError(error.response.data, "Network Error..")
        : "Network Error..";
      toast.error(apiMessage, { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FeeSystemMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/Dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/FeeSystem/FeeSystemDashboard">Fee System</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Generate Challan
                  </li>
                </ol>
              </nav>
              <div className="page-title">Generate Challan</div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handlePreview}>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Registration No</label>
                      <input
                        type="text"
                        className="form-control"
                        value={RegistrationNo}
                        onChange={(e) => setRegistrationNo(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Student ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={StudentId}
                        onChange={(e) => setStudentId(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <button
                      type="button"
                      className="button button-outline-primary w-100 mb-3"
                      onClick={handleSearch}
                      disabled={searchLoading}
                    >
                      {searchLoading ? "Searching..." : "Search Student"}
                    </button>
                  </div>
                </div>

                {studentInfo && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-4">
                          <div><strong>Name:</strong> {studentInfo.Student_Name}</div>
                          <div><strong>Father:</strong> {studentInfo.Father_Name}</div>
                          <div>
                            <strong>Registration No:</strong>{" "}
                            {studentInfo.Registration_No || "New Registration"}
                          </div>
                          <div><strong>Student ID:</strong> {studentInfo.Student_ID}</div>
                        </div>
                        <div className="col-md-4">
                          <div>
                            <strong>Class/Section:</strong>{" "}
                            {studentInfo.SelectedClass?.Class_Name || ""}
                            {studentInfo.SelectedSection?.Section_Name
                              ? ` (${studentInfo.SelectedSection.Section_Name})`
                              : ""}
                          </div>
                          <div>
                            <strong>Wing:</strong>{" "}
                            {studentInfo.SelectedClass?.SelectedWing?.Wing_Name || ""}
                          </div>
                          <div>
                            <strong>Transport:</strong>{" "}
                            {studentInfo.Current_Route_Id || "N/A"}
                          </div>
                          <div>
                            <strong>Transport Fee Type:</strong>{" "}
                            {studentInfo.Transport_Fee_Type || "N/A"}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div>
                            <strong>Concessions:</strong>{" "}
                            {studentConcessions.length > 0
                              ? studentConcessions
                                  .map(
                                    (c) =>
                                      c.Concession_Rule_Name ||
                                      c.Concession_Rule_Code ||
                                      c.Concession_Rule_ID
                                  )
                                  .join(", ")
                              : "N/A"}
                          </div>
                          <div>
                            <strong>Concession Amount:</strong>{" "}
                            {studentInfo.Concession_Amount || 0}
                          </div>
                          <div>
                            <strong>Concession Remarks:</strong>{" "}
                            {studentInfo.Concession_Remarks || ""}
                          </div>
                          <div>
                            <strong>Remaining Advance:</strong>{" "}
                            {RemainingAdvance > 0 ? RemainingAdvance : "0"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Month</label>
                      <select
                        className="form-select"
                        value={SelectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        required
                      >
                        <option value="">Please Select</option>
                        <MonthsDropDown />
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Year</label>
                      <select
                        className="form-select"
                        value={FYear}
                        onChange={(e) => setFYear(e.target.value)}
                        required
                      >
                        <option value="">Please Select</option>
                        <YearsDropDown />
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group mb-3">
                      <label>Challan Due Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={ChallanDueDate}
                        onChange={(e) => setChallanDueDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="addTransportFee"
                        checked={AddTransportFee}
                        onChange={(e) => setAddTransportFee(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="addTransportFee">
                        Add Transport Fee
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="adjustAdvanceOnChallan"
                        checked={AdjustAdvanceOnChallan}
                        onChange={(e) => setAdjustAdvanceOnChallan(e.target.checked)}
                        disabled={RemainingAdvance <= 0}
                      />
                      <label className="form-check-label" htmlFor="adjustAdvanceOnChallan">
                        Adjust advance on challan
                      </label>
                      <div className="small text-muted">
                        {RemainingAdvance > 0
                          ? `Uses student advance (up to Rs ${RemainingAdvance}) like monthly fee posting.`
                          : "No advance balance for this student."}
                      </div>
                    </div>
                  </div>
                </div>

                {AddTransportFee && (
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group mb-3">
                        <label>Bus Total Days</label>
                        <input
                          type="number"
                          className="form-control"
                          value={BusTotalDays}
                          onChange={(e) => setBusTotalDays(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group mb-3">
                        <label>Bus Avail Days</label>
                        <input
                          type="number"
                          className="form-control"
                          value={BusAvailDays}
                          onChange={(e) => setBusAvailDays(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-end">
                  <button
                    type="submit"
                    className="button button-outline-primary"
                    disabled={previewLoading || !studentInfo}
                  >
                    {previewLoading ? "Loading Preview..." : "Preview Challan"}
                  </button>
                </div>
              </form>

              {previewReady && (
                <div className="mt-4">
                  <h5 className="mb-3">Challan Preview</h5>
                  <p className="text-muted small">
                    Review and amend fee lines below. Add or remove fee heads as needed, then generate the challan.
                  </p>
                  <div className="table-responsive">
                    <table className="table table-theme table-sm">
                      <thead>
                        <tr>
                          <th>Fee Code</th>
                          <th>Description</th>
                          <th>Fee Posted</th>
                          <th>Concession</th>
                          <th>Arrears</th>
                          <th>Receivable</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewLines.map((row, index) => (
                          <tr key={row.Line_Id || index}>
                            <td>
                              <select
                                className="form-select form-select-sm"
                                value={row.Fee_Code || ""}
                                onChange={(e) =>
                                  handleFeeTypeChange(index, e.target.value)
                                }
                              >
                                <option value="">Select</option>
                                {availableFeeTypes.map((ft) => (
                                  <option
                                    key={ft.Fee_Type_ID}
                                    value={ft.Fee_Type_Code}
                                  >
                                    {ft.Fee_Type_Code} - {ft.Fee_Type_Name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={row.Description || ""}
                                onChange={(e) =>
                                  updatePreviewLine(index, "Description", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={row.Fee_Posted ?? 0}
                                onChange={(e) =>
                                  updatePreviewLine(index, "Fee_Posted", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={row.Concession_Amount ?? 0}
                                onChange={(e) =>
                                  updatePreviewLine(
                                    index,
                                    "Concession_Amount",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={row.Arrears ?? 0}
                                onChange={(e) =>
                                  updatePreviewLine(index, "Arrears", e.target.value)
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={row.Receiveable_Fee ?? 0}
                                onChange={(e) =>
                                  updatePreviewLine(
                                    index,
                                    "Receiveable_Fee",
                                    e.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="button button-danger button-sm"
                                onClick={() => handleRemovePreviewRow(index)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                        {previewLines.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center">
                              No fee lines. Add a fee head to continue.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={handleAddPreviewRow}
                    >
                      Add Fee Head
                    </button>
                    <div>
                      <strong>Total Receivable: </strong>
                      {previewTotal.toFixed(2)}
                    </div>
                  </div>

                  <div className="text-end mt-3">
                    <button
                      type="button"
                      className="button button-primary"
                      onClick={handleGenerate}
                      disabled={loading || previewLines.length === 0}
                    >
                      {loading ? "Generating..." : "Generate Challan"}
                    </button>
                  </div>
                </div>
              )}

              {generatedChallan && (
                <div className="alert alert-success mt-3">
                  Challan generated: <strong>{generatedChallan}</strong>
                  {lastAdvanceAdjusted > 0 && (
                    <div className="mt-1">
                      Advance adjusted: <strong>{lastAdvanceAdjusted}</strong>
                      {" · "}
                      Balance due on challan: <strong>{lastChallanBalance}</strong>
                    </div>
                  )}
                  <div className="mt-2">
                    <NavLink
                      to={`/students/PrintVoucher?ChallanNos=${generatedChallan}`}
                      target="_blank"
                      className="button button-primary button-sm"
                    >
                      Print Challan
                    </NavLink>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GenerateChallan;
